import { useEffect, useState } from "react";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { ToolGraphic } from "./ToolGraphics";
import { SHOWCASE_APPS, appUrl } from "./catalogue";
import { track } from "@/lib/forms";

const SLIDE_MS = 4800;

/** Rotating slide of AppsHub motion previews with a live tool-name list. */
export default function AppsShowcase({ compact = false }: { compact?: boolean }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const app = SHOWCASE_APPS[i];

  useEffect(() => {
    if (paused) return;
    const t = window.setTimeout(() => setI((n) => (n + 1) % SHOWCASE_APPS.length), SLIDE_MS);
    return () => window.clearTimeout(t);
  }, [i, paused]);

  return (
    <div
      className={`grid gap-6 ${compact ? "" : "lg:grid-cols-[0.8fr,1.2fr]"} items-stretch`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {!compact && (
        <ul className="flex flex-col gap-1.5" aria-label="Apps we have built">
          {SHOWCASE_APPS.map((a, idx) => {
            const active = idx === i;
            return (
              <li key={a.slug}>
                <button
                  type="button"
                  onClick={() => setI(idx)}
                  aria-current={active}
                  className={`relative w-full overflow-hidden rounded-2xl border px-5 py-4 text-left transition-colors ${
                    active ? "border-primary/40 bg-lightblue" : "border-transparent hover:bg-soft"
                  }`}
                >
                  <span className={`block text-[17px] font-semibold ${active ? "text-primary" : "text-navy"}`}>{a.title}</span>
                  <span className="mt-0.5 block text-[13px] text-muted-foreground">{a.category} · {a.access.join(" · ")}</span>
                  {active && !paused && (
                    <span key={i} className="absolute bottom-0 left-0 h-0.5 bg-primary animate-[showcase-progress_linear_forwards]" style={{ animationDuration: `${SLIDE_MS}ms` }} />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <article className="card-atk flex flex-col overflow-hidden !p-0" aria-live="polite">
        <div className="flex items-center justify-between border-b border-border px-6 py-3">
          <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {String(i + 1).padStart(2, "0")} / {String(SHOWCASE_APPS.length).padStart(2, "0")}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setI((n) => (n - 1 + SHOWCASE_APPS.length) % SHOWCASE_APPS.length)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-navy transition-colors hover:border-primary/40 hover:text-primary"
              aria-label="Previous app"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setI((n) => (n + 1) % SHOWCASE_APPS.length)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-navy transition-colors hover:border-primary/40 hover:text-primary"
              aria-label="Next app"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div key={app.slug} className="animate-fade-up flex flex-1 flex-col">
          <div className="bg-white px-6 pt-8 pb-6 sm:px-10">
            <ToolGraphic graphicKey={app.graphic_key} variant="card" />
          </div>
          <div className="mt-auto border-t border-border p-6 sm:px-8">
            <h3 className="text-[22px] font-bold text-navy">{app.title}</h3>
            <p className="mt-1.5 text-[15px] text-muted-foreground">{app.summary}</p>
            <a
              href={appUrl(app.slug)}
              target="_blank"
              rel="noopener"
              onClick={() => track("apps_showcase_tool_click", { tool: app.slug })}
              className="text-cta mt-4"
            >
              View on ATK AppsHub <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
        {compact && (
          <div className="flex justify-center gap-1.5 pb-5">
            {SHOWCASE_APPS.map((a, idx) => (
              <button key={a.slug} onClick={() => setI(idx)} aria-label={`Show ${a.title}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-primary" : "w-1.5 bg-border"}`} />
            ))}
          </div>
        )}
      </article>
    </div>
  );
}
